import React, { useEffect, useState } from 'react';
import { Package, MapPin, Loader2, Navigation, ScanLine, Camera, CheckCircle2, RotateCcw } from 'lucide-react';
import { driverApi } from '../services/api';
import api from '../services/api';
import DriverRoutePanel from './DriverRoutePanel';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import ShopAnywhereCallout from './ShopAnywhereCallout';
import QrPickupScanner from './QrPickupScanner';
import PodPhotoUploader from './PodPhotoUploader';
import NavigateDialog from '../components/NavigateDialog';
import { toast } from 'sonner';

const STATUS_COLORS = {
  placed: 'bg-slate-100 text-slate-700',
  payment_confirmed: 'bg-cyan-50 text-cyan-700',
  confirmed: 'bg-blue-50 text-blue-700',
  preparing: 'bg-amber-50 text-amber-700',
  ready: 'bg-violet-50 text-violet-700',
  accepted: 'bg-blue-50 text-blue-700',
  en_route_to_vendor: 'bg-sky-50 text-sky-700',
  arrived_at_vendor: 'bg-indigo-50 text-indigo-700',
  picked_up: 'bg-amber-50 text-amber-700',
  on_the_way: 'bg-emerald-50 text-emerald-700',
  arrived_at_customer: 'bg-teal-50 text-teal-700',
  delivered: 'bg-slate-100 text-slate-500',
  cancelled: 'bg-rose-50 text-rose-700',
};

// Suggested next actions per current status (driver workflow)
const NEXT_ACTIONS = {
  ready: [{ status: 'accepted', label: 'Accept order' }],
  accepted: [{ status: 'en_route_to_vendor', label: 'Start pickup' }],
  en_route_to_vendor: [{ status: 'arrived_at_vendor', label: 'Arrived at vendor' }],
  picked_up: [{ status: 'on_the_way', label: 'On the way' }],
  on_the_way: [{ status: 'arrived_at_customer', label: 'Arrived at customer' }],
};

const wazeUrl = (lat, lng) =>
  lat != null && lng != null ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes` : null;

// Rough great-circle distance in metres between two lat/lng pairs.
// Not exact but plenty accurate for proximity nudges (< 5km).
const distMetres = (lat1, lng1, lat2, lng2) => {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null;
  const R = 6371000;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

const DriverDeliveries = () => {
  const [orders, setOrders] = useState(null);
  const [scanning, setScanning] = useState(null); // order.id being scanned
  const [uploadingPod, setUploadingPod] = useState(null); // order.id
  const [skipDialog, setSkipDialog] = useState({ open: false, orderId: null, reason: '', busy: false });
  // In-app navigation dialog (Google Maps Embed).
  const [navTarget, setNavTarget] = useState(null); // { lat, lng, address, label } | null
  // Track driver's own current GPS so the embedded map starts from where they are.
  const [myGps, setMyGps] = useState(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => setMyGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 },
    );
    return () => navigator.geolocation.clearWatch?.(id);
  }, []);

  const load = () => driverApi.orders().then(setOrders).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const advance = async (id, status, extra = null) => {
    try {
      await driverApi.updateOrderStatus(id, status, extra);
      toast.success(`Marked ${status.replace(/_/g, ' ')}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const release = async (id) => {
    if (!window.confirm('Release this order back to the pool?\n\nAnother driver in your zone can pick it up. You will not be able to re-claim this specific order.')) return;
    try {
      await driverApi.releaseOrder(id);
      toast.success('Order released — thanks for the heads-up.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not release'); }
  };

  const markDelivered = (order) => {
    const podCount = order.pod_photos?.length || 0;
    // 'Meet driver at door' orders never need POD photos.
    if (order.dropoff_preference === 'meet_at_door' || podCount >= 2) {
      advance(order.id, 'delivered');
    } else {
      setSkipDialog({ open: true, orderId: order.id, reason: '', busy: false });
    }
  };

  const submitSkipDelivered = async () => {
    const reason = skipDialog.reason.trim();
    if (reason.length < 5) { toast.error('Please provide at least 5 characters explaining why.'); return; }
    setSkipDialog((s) => ({ ...s, busy: true }));
    try {
      const resp = await driverApi.updateOrderStatus(skipDialog.orderId, 'delivered', { pod_skip_reason: reason });
      toast.success('Order marked delivered (no-photo override recorded). Admin has been notified.');
      if (resp?._admin_wa_link) {
        toast('WhatsApp admin about this override?', {
          duration: 10000,
          action: {
            label: 'Open WhatsApp',
            onClick: () => window.open(resp._admin_wa_link, '_blank', 'noopener'),
          },
        });
      }
      setSkipDialog({ open: false, orderId: null, reason: '', busy: false });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
      setSkipDialog((s) => ({ ...s, busy: false }));
    }
  };

  if (orders === null) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">My deliveries</h1>
      <p className="text-slate-500 mt-1">Navigate, pickup with QR, and complete deliveries.</p>
      <div className="mt-6">
        <DriverRoutePanel />
      </div>
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {orders.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <Package className="h-8 w-8 text-slate-300 mx-auto" />
            <p className="mt-2 text-sm">No deliveries yet.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {orders.map((o) => {
              // Simplified: "before pickup" = anything after Accept but before
              // the QR scan; "after pickup" = anything after QR scan but before
              // delivered. No more granular en_route/arrived states.
              const isBeforePickup = ['ready', 'accepted', 'en_route_to_vendor', 'arrived_at_vendor'].includes(o.status);
              const isAfterPickup = ['picked_up', 'on_the_way', 'arrived_at_customer'].includes(o.status);
              const vendorWaze = wazeUrl(o.vendor_lat, o.vendor_lng);
              const destWaze = wazeUrl(o.dest_lat, o.dest_lng);
              return (
                <li key={o.id} className="p-5" data-testid={`driver-order-${o.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center"><Package className="h-5 w-5" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900 truncate">{o.vendor_name || o.vendorName}</h3>
                        <Badge className={`${STATUS_COLORS[o.status] || 'bg-slate-100'} border-0 hover:opacity-100 capitalize`}>{(o.status || 'placed').replace(/_/g, ' ')}</Badge>
                        <ShopAnywhereCallout order={o} compact />
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 font-mono">{o.id}</div>
                      <div className="mt-2 text-sm text-slate-600 flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{o.address}</div>
                      <ShopAnywhereCallout order={o} />
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-slate-900">${(o.total || 0).toFixed(2)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{o.placedAt ? new Date(o.placedAt).toLocaleDateString() : '—'}</div>
                    </div>
                  </div>
                  {/* Mobile-only hero action card — one dominant CTA at a
                      time so the driver never has to hunt for what's next. */}
                  {o.status !== 'delivered' && o.status !== 'cancelled' && (isBeforePickup || isAfterPickup) && (() => {
                    // For logistics / courier / same-day orders the pickup
                    // point is at `pickup_lat/pickup_lng` (sender's address),
                    // not the vendor's registered warehouse.
                    const puLat = o.pickup_lat != null ? o.pickup_lat : o.vendor_lat;
                    const puLng = o.pickup_lng != null ? o.pickup_lng : o.vendor_lng;
                    const puAddr = o.pickup_address || o.vendor_address || 'Pickup';
                    const targetLat = isBeforePickup ? puLat : o.dest_lat;
                    const targetLng = isBeforePickup ? puLng : o.dest_lng;
                    const d = distMetres(myGps?.lat, myGps?.lng, targetLat, targetLng);
                    const isNear = d != null && d <= 300;
                    const distTxt = d == null ? null : (d < 1000 ? `${Math.round(d)} m` : `${(d / 1000).toFixed(1)} km`);
                    const pickupLabel = isBeforePickup ? 'to pickup' : 'to customer';
                    return (
                    <div className="sm:hidden mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-2.5" data-testid={`driver-hero-action-${o.id}`}>
                      {isNear && (
                        <div
                          className={`rounded-lg px-3 py-2 text-[12px] font-semibold text-center ${isBeforePickup ? 'bg-amber-100 text-amber-900 border border-amber-300' : 'bg-emerald-100 text-emerald-900 border border-emerald-300'} animate-pulse`}
                          data-testid={`driver-proximity-hint-${o.id}`}
                        >
                          {isBeforePickup
                            ? `You're ~${distTxt} from pickup — get ready to scan.`
                            : `You're ~${distTxt} from the customer — get the camera ready.`}
                        </div>
                      )}
                      {!isNear && distTxt && (
                        <div className="text-[11px] text-center text-slate-500" data-testid={`driver-proximity-dist-${o.id}`}>
                          {distTxt} away · {pickupLabel}
                        </div>
                      )}
                      {isBeforePickup && (
                        <>
                          {(puLat != null || puAddr) && (
                            <button
                              type="button"
                              onClick={() => setNavTarget({
                                lat: puLat, lng: puLng,
                                address: puAddr, label: 'Pickup',
                              })}
                              data-testid={`driver-hero-navigate-vendor-${o.id}`}
                              className="w-full h-14 rounded-xl bg-slate-900 text-white font-semibold flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition-transform"
                            >
                              <Navigation className="h-5 w-5" /> Navigate to pickup
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setScanning(o.id)}
                            data-testid={`driver-hero-scan-${o.id}`}
                            className={`w-full h-14 rounded-xl text-white font-semibold flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition-transform ${isNear ? 'bg-emerald-500 hover:bg-emerald-600 ring-2 ring-emerald-300 ring-offset-2' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                          >
                            <ScanLine className="h-5 w-5" /> Scan QR to pick up
                          </button>
                          <p className="text-[11px] text-center text-slate-500 pt-0.5">The sender or vendor will show you a QR code — scan it to confirm pickup.</p>
                        </>
                      )}
                      {isAfterPickup && (
                        <>
                          {(o.dest_lat != null || o.address) && (
                            <button
                              type="button"
                              onClick={() => setNavTarget({
                                lat: o.dest_lat, lng: o.dest_lng,
                                address: o.address || 'Customer address', label: 'Customer drop-off',
                              })}
                              data-testid={`driver-hero-navigate-customer-${o.id}`}
                              className="w-full h-14 rounded-xl bg-slate-900 text-white font-semibold flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition-transform"
                            >
                              <Navigation className="h-5 w-5" /> Navigate to customer
                            </button>
                          )}
                          {o.dropoff_preference === 'meet_at_door' ? (
                            <div className="w-full rounded-xl bg-blue-50 border border-blue-200 text-blue-800 text-sm font-medium flex items-center justify-center gap-2 py-3" data-testid={`driver-hero-meet-${o.id}`}>
                              Customer will meet you at the door — no photos needed
                            </div>
                          ) : (
                          <button
                            type="button"
                            onClick={() => setUploadingPod(o.id)}
                            data-testid={`driver-hero-pod-${o.id}`}
                            className={`w-full h-14 rounded-xl text-white font-semibold flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition-transform ${isNear ? 'bg-emerald-500 hover:bg-emerald-600 ring-2 ring-emerald-300 ring-offset-2' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                          >
                            <Camera className="h-5 w-5" /> Take / upload 2 delivery photos ({o.pod_photos?.length || 0}/2)
                          </button>
                          )}
                          <button
                            type="button"
                            disabled={o.status === 'delivered'}
                            onClick={() => markDelivered(o)}
                            data-testid={`driver-hero-delivered-${o.id}`}
                            className="w-full h-14 rounded-xl bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold tracking-wider flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition-transform"
                          >
                            <CheckCircle2 className="h-5 w-5" /> DELIVERED
                          </button>
                          <p className="text-[11px] text-center text-slate-500 pt-0.5">Photos are optional — tap DELIVERED whenever the order is complete.</p>
                        </>
                      )}
                    </div>
                    );
                  })()}

                  {/* Action bar */}
                  {o.status !== 'delivered' && o.status !== 'cancelled' && (
                    <div className="mt-3 hidden sm:flex items-center gap-2 flex-wrap">
                      {isBeforePickup && (o.vendor_lat != null || o.pickup_lat != null || o.pickup_address) && (
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setNavTarget({
                              lat: o.pickup_lat != null ? o.pickup_lat : o.vendor_lat,
                              lng: o.pickup_lng != null ? o.pickup_lng : o.vendor_lng,
                              address: o.pickup_address || o.vendor_address || 'Pickup',
                              label: 'Pickup',
                            })}
                            data-testid={`driver-navigate-vendor-${o.id}`}
                            className="inline-flex items-center gap-1 text-xs font-medium px-3 h-9 rounded-lg bg-slate-900 hover:bg-slate-800 text-white"
                          >
                            <Navigation className="h-3 w-3" /> Navigate to pickup
                          </button>
                          {(o.pickup_lat != null ? wazeUrl(o.pickup_lat, o.pickup_lng) : vendorWaze) && (
                            <a href={o.pickup_lat != null ? wazeUrl(o.pickup_lat, o.pickup_lng) : vendorWaze} target="_blank" rel="noreferrer" data-testid={`driver-waze-vendor-${o.id}`}
                               className="inline-flex items-center h-9 px-2 text-[11px] rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
                              Waze
                            </a>
                          )}
                        </div>
                      )}
                      {isAfterPickup && (o.dest_lat != null || o.address) && (
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setNavTarget({
                              lat: o.dest_lat, lng: o.dest_lng,
                              address: o.address || 'Customer address', label: 'Customer drop-off',
                            })}
                            data-testid={`driver-navigate-customer-${o.id}`}
                            className="inline-flex items-center gap-1 text-xs font-medium px-3 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <Navigation className="h-3 w-3" /> Navigate to customer
                          </button>
                          {destWaze && (
                            <a href={destWaze} target="_blank" rel="noreferrer" data-testid={`driver-waze-customer-${o.id}`}
                               className="inline-flex items-center h-9 px-2 text-[11px] rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
                              Waze
                            </a>
                          )}
                        </div>
                      )}
                      {(NEXT_ACTIONS[o.status] || []).map((act) => (
                        <Button key={act.status} size="sm" variant="outline"
                          onClick={() => advance(o.id, act.status)}
                          data-testid={`driver-advance-${o.id}-${act.status}`}
                        >
                          {act.label}
                        </Button>
                      ))}
                      {['ready', 'accepted', 'confirmed'].includes(o.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => release(o.id)}
                          className="text-amber-700 border-amber-200 hover:bg-amber-50"
                          data-testid={`driver-release-${o.id}`}
                          title="Release this order back to the pool for other drivers"
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />Release
                        </Button>
                      )}
                      {['accepted', 'en_route_to_vendor', 'arrived_at_vendor'].includes(o.status) && (
                        <Button size="sm" onClick={() => setScanning(o.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`driver-scan-${o.id}`}>
                          <ScanLine className="h-3 w-3 mr-1" /> Scan QR to pick up
                        </Button>
                      )}
                      {['picked_up', 'on_the_way', 'arrived_at_customer'].includes(o.status) && o.dropoff_preference !== 'meet_at_door' && (o.pod_photos?.length || 0) < 2 && (
                        <Button size="sm" onClick={() => setUploadingPod(o.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`driver-pod-${o.id}`}>
                          <Camera className="h-3 w-3 mr-1" /> Upload 2 delivery photos ({o.pod_photos?.length || 0}/2)
                        </Button>
                      )}
                      {['picked_up', 'on_the_way', 'arrived_at_customer'].includes(o.status) && o.dropoff_preference === 'meet_at_door' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[11px] font-medium" data-testid={`driver-meet-chip-${o.id}`}>
                          Meet at door — no photos needed
                        </span>
                      )}
                      {['picked_up', 'on_the_way', 'arrived_at_customer'].includes(o.status) && (o.pod_photos?.length || 0) >= 2 && (
                        <Button
                          size="sm"
                          onClick={() => advance(o.id, 'delivered')}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                          data-testid={`driver-mark-delivered-${o.id}`}
                          title="Both photos uploaded — confirm the delivery is complete"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          Delivered
                        </Button>
                      )}
                      {['picked_up', 'on_the_way', 'arrived_at_customer'].includes(o.status) && (o.pod_photos?.length || 0) < 2 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markDelivered(o)}
                          className="text-amber-700 border-amber-200 hover:bg-amber-50"
                          data-testid={`driver-deliver-${o.id}`}
                          title="Override — no photos: you'll be asked for a short reason"
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Delivered (no photo)
                        </Button>
                      )}
                    </div>
                  )}
                  {/* Pipeline toggles removed — simplified flow only exposes
                      Accept → QR scan → 2 POD photos → Delivered. Advanced
                      pipeline toggles are kept in `OrderStatusToggles` and can
                      still be re-enabled here in the future if we ever need
                      edge-case overrides in the driver UI. */}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {scanning && (
        <QrPickupScanner
          orderId={scanning}
          open={!!scanning}
          onClose={() => setScanning(null)}
          onSuccess={() => { setScanning(null); load(); }}
        />
      )}

      <NavigateDialog
        open={Boolean(navTarget)}
        onClose={() => setNavTarget(null)}
        origin={myGps}
        destination={navTarget}
      />
      {uploadingPod && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setUploadingPod(null)}>
          <div className="bg-white rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-900 mb-3">Upload 2 proof-of-delivery photos</h3>
            <PodPhotoUploader
              orderId={uploadingPod}
              value={(orders.find((o) => o.id === uploadingPod)?.pod_photos) || []}
              onChange={() => load()}
            />
            <div className="mt-4 flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => setUploadingPod(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {skipDialog.open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !skipDialog.busy && setSkipDialog({ open: false, orderId: null, reason: '', busy: false })} data-testid="skip-pod-dialog">
          <div className="bg-white rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-amber-600" /> Mark delivered without photos?
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Photos are the strongest proof of delivery. If you truly can&rsquo;t upload right now, tell us why — this note is saved on the order for the customer and admin to see.
            </p>
            <label className="text-xs text-slate-600 font-medium">Why no photo?</label>
            <textarea
              value={skipDialog.reason}
              onChange={(e) => setSkipDialog((s) => ({ ...s, reason: e.target.value }))}
              placeholder="e.g. Customer signed on paper, camera not working, poor connection..."
              rows={3}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-slate-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 focus:outline-none resize-none"
              data-testid="skip-pod-reason-input"
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSkipDialog({ open: false, orderId: null, reason: '', busy: false })} disabled={skipDialog.busy}>Cancel</Button>
              <Button
                size="sm"
                onClick={submitSkipDelivered}
                disabled={skipDialog.busy || skipDialog.reason.trim().length < 5}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="skip-pod-submit-btn"
              >
                {skipDialog.busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                Mark delivered
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDeliveries;
